import { Dispatch, SetStateAction } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { CompanyDto } from 'tdc-web-backend/companies/schemas';
import { EnumLanguage, mapLanguage } from 'tdc-web-backend/enums/enums';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { useRefresh } from '../../../../../../../../../../utils/hooks/crud-hooks/useRefresh';
import ConfirmModal from '../../../../../../../../../../components/confirm-modal/ConfirmModal';
import CustomInput from '../../../../../../../../../../components/custom-input/CustomInput';
import { ReactComponent as LanguagesIcon } from '../../../../../../../../../../assets/icons/working-languages-icon.svg';
import { primaryLight } from '../../../../../../../../../../utils/color';
import useUserCompany from '../../../../../../../../../../utils/hooks/useUserCompany';

interface CompanyWorkingLanguagesModalProps {
  isEditModalOpen: boolean;
  setIsEditModalOpen: Dispatch<SetStateAction<boolean>>;
}

const CompanyWorkingLanguagesEditModal = ({
  isEditModalOpen,
  setIsEditModalOpen,
}: CompanyWorkingLanguagesModalProps) => {
  const refresh = useRefresh();
  const { company: companyData, updateCompany, isLoadingUpdateCompany } = useUserCompany();
  const { t } = useTranslation();
  const methods = useForm<Pick<CompanyDto, 'languages'>>({
    mode: 'onChange',
    defaultValues: {
      languages: companyData?.languages ?? ([] as EnumLanguage[]),
    },
  });

  const onSubmit = (data: Pick<CompanyDto, 'languages'>) => {
    updateCompany(data, {
      onSuccess: () => {
        refresh();
        setIsEditModalOpen(false);
      },
    });
  };

  return (
    <ConfirmModal
      width="40%"
      isModalOpen={isEditModalOpen}
      setIsModalOpen={setIsEditModalOpen}
      title={t('settings.companyProfile.workingLanguages')}
      onCancel={() => setIsEditModalOpen(false)}
      onConfirm={methods.handleSubmit(onSubmit)}
      confirmButtonText={t('common.save')}
      automaticallyCloseModalOnButtonClick={false}
      isLoading={isLoadingUpdateCompany}
      message={
        <FormProvider {...methods}>
          <Box component="form">
            <CustomInput
              input={<CustomInput.Autocomplete />}
              choices={mapLanguage}
              translation={{ ns: 'enum' }}
              name="languages"
              label={t('common.languages')}
              multiple
              placeholder={t('common.select')}
              sx={{ width: '100%' }}
              required
            />
          </Box>
        </FormProvider>
      }
      iconProps={{
        icon: <LanguagesIcon fill={primaryLight[500]} />,
        wrapperBackgroundColor: primaryLight[100],
      }}
    />
  );
};

export default CompanyWorkingLanguagesEditModal;
